import type { CartItem } from '@features/cart/types';
import {
  ANONYMOUS_CART_OWNER,
  COMMERCE_CART_SCHEMA_VERSION,
  type CartOwnerId,
  type StoredCommerceCart,
} from './cartTypes';
import { mergeCartItems, normalizeCartItems } from './cartEngine';
import { mergeAnonymousCartIntoUserCart } from './cartMerge';

const CHANNEL_NAME = 'commerce.cart.v2';
const LEGACY_CART_KEY = 'cart';

function ownerSegment(ownerId: CartOwnerId): string {
  return ownerId ? ownerId : ANONYMOUS_CART_OWNER;
}

export function cartStorageKey(ownerId: CartOwnerId): string {
  return `commerce.cart.v${COMMERCE_CART_SCHEMA_VERSION}:${ownerSegment(ownerId)}`;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function readLegacyCart(): CartItem[] {
  const storage = getStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(LEGACY_CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? normalizeCartItems(parsed as CartItem[]) : [];
  } catch {
    return [];
  }
}

export function readCartItems(ownerId: CartOwnerId): CartItem[] {
  const storage = getStorage();
  if (!storage) return [];

  try {
    const raw = storage.getItem(cartStorageKey(ownerId));
    if (!raw) {
      return ownerId ? [] : readLegacyCart();
    }
    const parsed = JSON.parse(raw) as Partial<StoredCommerceCart>;
    if (parsed.version !== COMMERCE_CART_SCHEMA_VERSION) return [];
    return normalizeCartItems(parsed.items ?? []);
  } catch {
    return [];
  }
}

function writeEnvelope(ownerId: CartOwnerId, items: readonly CartItem[]): void {
  const storage = getStorage();
  if (!storage) return;

  const owner = ownerSegment(ownerId);
  const payload: StoredCommerceCart = {
    version: COMMERCE_CART_SCHEMA_VERSION,
    owner,
    updatedAt: new Date().toISOString(),
    items: normalizeCartItems([...items]),
  };

  storage.setItem(cartStorageKey(ownerId), JSON.stringify(payload));
}

function notifyCartChange(ownerId: CartOwnerId): void {
  if (typeof window === 'undefined') return;
  const detail = { owner: ownerSegment(ownerId), key: cartStorageKey(ownerId) };
  window.dispatchEvent(new CustomEvent(CHANNEL_NAME, { detail }));

  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(detail);
    channel.close();
  }
}

export function writeCartItems(
  ownerId: CartOwnerId,
  items: readonly CartItem[]
): CartItem[] {
  const normalized = normalizeCartItems([...items]);
  try {
    writeEnvelope(ownerId, normalized);
    notifyCartChange(ownerId);
  } catch (error) {
    console.error('[cartStorage] write failed', error);
  }
  return normalized;
}

export function mergeAnonymousCartIntoOwner(ownerId: string): CartItem[] {
  const anonymousItems = readCartItems(null);
  const userItems = readCartItems(ownerId);
  const merged = mergeAnonymousCartIntoUserCart(userItems, anonymousItems).items;
  writeCartItems(ownerId, merged);
  if (anonymousItems.length > 0) {
    writeCartItems(null, []);
  }
  return merged;
}

export function subscribeCartStorage(
  ownerId: CartOwnerId,
  onChange: (items: CartItem[]) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const owner = ownerSegment(ownerId);
  const key = cartStorageKey(ownerId);

  const handleStorage = (event: StorageEvent) => {
    if (event.key === key) onChange(readCartItems(ownerId));
  };

  const handleCustom = (event: Event) => {
    const custom = event as CustomEvent<{ owner?: string }>;
    if (custom.detail?.owner === owner) onChange(readCartItems(ownerId));
  };

  let channel: BroadcastChannel | null = null;
  const handleBroadcast = (event: MessageEvent<{ owner?: string }>) => {
    if (event.data?.owner === owner) onChange(readCartItems(ownerId));
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(CHANNEL_NAME, handleCustom);
  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener('message', handleBroadcast);
  }

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(CHANNEL_NAME, handleCustom);
    if (channel) {
      channel.removeEventListener('message', handleBroadcast);
      channel.close();
    }
  };
}

export function appendCartItems(ownerId: CartOwnerId, items: readonly CartItem[]): CartItem[] {
  return writeCartItems(ownerId, mergeCartItems(readCartItems(ownerId), items));
}
